"use client";

import { type HTMLAttributes, useState, type FC, type Ref } from "react";

import { Card, type CardProps } from "../Card";
import { Checkbox } from "../Checkbox";
import { Text } from "../Text";

import { contentStyle, checkboxStyle, labelStyle } from "./OptionalCard.css";
import { ProgressBar } from "../ProgressBar";

export type OptionalCardProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "onChange" | "onClick"
> &
  Pick<CardProps, "disabled" | "applyMotion" | "variant"> & {
    /**
     * The label text displayed on the card
     */
    label: string;
    /**
     * Whether the card is checked/selected
     */
    value?: boolean;
    /**
     * Whether the card is loading
     */
    loading?: boolean;
    /**
     * Callback fired when the checked state changes
     */
    onChange?: (checked: boolean) => void;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * OptionalCard is a controlled clickable card with a checkbox that allows users to toggle an optional selection.
 */
export const OptionalCard: FC<OptionalCardProps> = ({
  label,
  value = false,
  disabled = false,
  loading = false,
  variant = "primary",
  onChange,
  ref,
  ...props
}) => {
  const [hasHover, setHasHover] = useState(false);

  const handleClick = () => {
    if (disabled) {
      return;
    }
    onChange?.(!value);
  };

  return (
    <Card
      ref={ref}
      contentClassName={contentStyle}
      onClick={handleClick}
      variant={variant}
      disabled={disabled}
      border="default"
      padding="s"
      gap="s"
      borderRadius="xs"
      onMouseEnter={() => setHasHover(true)}
      onMouseLeave={() => setHasHover(false)}
      trailing={
        loading && <ProgressBar variant="infinite" position="absolute" />
      }
      {...props}
    >
      <Checkbox
        className={checkboxStyle}
        checked={value}
        disabled={disabled}
        size="m"
        tabIndex={-1}
        active={hasHover}
      />

      <Text variant="bodyS" className={labelStyle}>
        {label}
      </Text>
    </Card>
  );
};
